import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Client, StompSubscription } from '@stomp/stompjs';
import { API_BASE_URL } from '../services/config';
import notificationSound from '../assets/sound.wav';
import toast from 'react-hot-toast';
import {
  OrderRequestNotification,
  WaiterCallNotification,
  orderRequestService,
} from '../services/orderRequest';

// Derive WS URL from API base URL: http(s):// → ws(s)://
const WS_URL = API_BASE_URL.replace(/^http/, 'ws') + '/ws';

type PopupItem =
  | { type: 'order-request'; data: OrderRequestNotification }
  | { type: 'waiter-call'; data: WaiterCallNotification };

interface WebSocketContextValue {
  connected: boolean;
  subscribe: (topic: string, handler: (msg: any) => void) => string;
  unsubscribe: (subId: string) => void;
}

const WebSocketContext = createContext<WebSocketContextValue>({
  connected: false,
  subscribe: () => '',
  unsubscribe: () => {},
});

export const useWebSocket = () => useContext(WebSocketContext);

interface Props {
  restaurantId: string | null;
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<Props> = ({ restaurantId, children }) => {
  const [connected, setConnected] = useState(false);
  const [popupQueue, setPopupQueue] = useState<PopupItem[]>([]);
  const [actioning, setActioning] = useState(false);
  const clientRef = useRef<Client | null>(null);
  const handlersRef = useRef<Map<string, { topic: string; handler: (msg: any) => void; sub?: StompSubscription }>>(new Map());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const prevQueueLengthRef = useRef(0);

  // Pre-load notification sound
  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = 0.9;
    audioRef.current.loop = true;
  }, []);

  // Loop sound while popup queue is non-empty; stop when queue clears
  useEffect(() => {
    const prev = prevQueueLengthRef.current;
    const curr = popupQueue.length;
    prevQueueLengthRef.current = curr;
    if (!audioRef.current) return;
    if (curr > 0 && prev === 0) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } else if (curr === 0 && prev > 0) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [popupQueue.length]);

  useEffect(() => {
    if (!restaurantId) return;

    const token = localStorage.getItem('accessToken') ?? '';
    const base = `/topic/restaurant/${restaurantId}`;

    const client = new Client({
      brokerURL: WS_URL,
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 5000,
      onConnect: () => {
        setConnected(true);

        // App-wide sound + toast + popup for new QR orders and waiter calls
        client.subscribe(`${base}/notifications`, (frame) => {
          const msg = JSON.parse(frame.body);
          if (msg.type === 'ORDER_REQUEST_NEW') {
            toast(`🪑 New order request from ${msg.payload?.tableName || 'a table'}`, {
              duration: 6000,
              style: { background: '#1D4ED8', color: '#fff', fontWeight: '600' },
            });
            setPopupQueue(q => [...q, { type: 'order-request', data: msg.payload as OrderRequestNotification }]);
          } else if (msg.type === 'WAITER_CALL_NEW') {
            toast(`🔔 Waiter called at ${msg.payload?.tableName || 'a table'}`, {
              duration: 6000,
              style: { background: '#D97706', color: '#fff', fontWeight: '600' },
            });
            setPopupQueue(q => [...q, { type: 'waiter-call', data: msg.payload as WaiterCallNotification }]);
          } else if (msg.type === 'NOTIFICATION_STATUS_CHANGE') {
            // Auto-dismiss popup on all clients when any user acts on it
            const { id } = msg.payload;
            setPopupQueue(q => q.filter(item => item.data.id !== id));
          }
        });

        // Re-subscribe any existing subscriptions registered before connect
        handlersRef.current.forEach((entry, subId) => {
          const sub = client.subscribe(entry.topic, (frame) => {
            entry.handler(JSON.parse(frame.body));
          });
          handlersRef.current.set(subId, { ...entry, sub });
        });
      },
      onDisconnect: () => setConnected(false),
    });

    client.activate();
    clientRef.current = client;

    return () => {
      client.deactivate();
      clientRef.current = null;
      setConnected(false);
    };
  }, [restaurantId]);

  const subscribe = useCallback((topic: string, handler: (msg: any) => void): string => {
    const subId = `sub-${Date.now()}-${Math.random()}`;
    const entry = { topic, handler };

    if (clientRef.current?.connected) {
      const sub = clientRef.current.subscribe(topic, (frame) => {
        handler(JSON.parse(frame.body));
      });
      handlersRef.current.set(subId, { ...entry, sub });
    } else {
      // Will be activated on connect
      handlersRef.current.set(subId, entry);
    }

    return subId;
  }, []);

  const unsubscribe = useCallback((subId: string) => {
    const entry = handlersRef.current.get(subId);
    if (entry?.sub) {
      entry.sub.unsubscribe();
    }
    handlersRef.current.delete(subId);
  }, []);

  const dismissTop = useCallback(() => {
    setPopupQueue(q => q.slice(1));
  }, []);

  const handleAccept = useCallback(async (id: string) => {
    setActioning(true);
    try {
      await orderRequestService.acceptRequest(id);
      // NOTIFICATION_STATUS_CHANGE WS broadcast auto-dismisses on all clients
    } catch {
      // On API error, dismiss locally so user isn't stuck
      setPopupQueue(q => q.filter(item => item.data.id !== id));
    } finally {
      setActioning(false);
    }
  }, []);

  const handleReject = useCallback(async (id: string) => {
    setActioning(true);
    try {
      await orderRequestService.rejectRequest(id);
    } catch {
      setPopupQueue(q => q.filter(item => item.data.id !== id));
    } finally {
      setActioning(false);
    }
  }, []);

  const handleAcknowledge = useCallback(async (id: string) => {
    setActioning(true);
    try {
      await orderRequestService.acknowledgeWaiterCall(id);
    } catch {
      setPopupQueue(q => q.filter(item => item.data.id !== id));
    } finally {
      setActioning(false);
    }
  }, []);

  const topPopup = popupQueue[0];

  return (
    <WebSocketContext.Provider value={{ connected, subscribe, unsubscribe }}>
      {children}
      {topPopup && (
        <NotificationPopup
          item={topPopup}
          queueLength={popupQueue.length}
          actioning={actioning}
          onAccept={handleAccept}
          onReject={handleReject}
          onAcknowledge={handleAcknowledge}
          onDismiss={dismissTop}
        />
      )}
    </WebSocketContext.Provider>
  );
};

// ── Global Notification Popup ─────────────────────────────────────────────────

interface PopupProps {
  item: PopupItem;
  queueLength: number;
  actioning: boolean;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onAcknowledge: (id: string) => void;
  onDismiss: () => void;
}

const NotificationPopup: React.FC<PopupProps> = ({
  item,
  queueLength,
  actioning,
  onAccept,
  onReject,
  onAcknowledge,
  onDismiss,
}) => {
  const isOrderRequest = item.type === 'order-request';
  const req = isOrderRequest ? (item.data as OrderRequestNotification) : null;
  const call = !isOrderRequest ? (item.data as WaiterCallNotification) : null;

  const total = req ? req.items.reduce((s, i) => s + i.unitPrice * i.quantity, 0) : 0;

  // Prevent background scroll while popup is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.55)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        animation: 'fadeIn 0.15s ease',
      }}
      onClick={e => { if (e.target === e.currentTarget) onDismiss(); }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '420px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          animation: 'slideUp 0.2s ease',
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: isOrderRequest ? '#1D4ED8' : '#D97706',
            padding: '14px 18px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>{isOrderRequest ? '🪑' : '🔔'}</span>
            <div>
              <p style={{ color: '#fff', fontWeight: '700', fontSize: '15px', margin: 0 }}>
                {isOrderRequest ? 'New Order Request' : 'Waiter Called'}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', margin: 0 }}>
                {isOrderRequest ? req!.tableName || req!.tableId : call!.tableName}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {queueLength > 1 && (
              <span
                style={{
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: '700',
                  padding: '2px 8px',
                  borderRadius: '99px',
                }}
              >
                +{queueLength - 1} more
              </span>
            )}
            <button
              onClick={onDismiss}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none', borderRadius: '99px',
                color: '#fff', cursor: 'pointer',
                width: '28px', height: '28px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '16px', fontWeight: '700',
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 18px' }}>
          {isOrderRequest && req ? (
            <>
              {/* Customer info */}
              <div
                style={{
                  backgroundColor: '#F0F7FF',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  marginBottom: '14px',
                }}
              >
                <p style={{ margin: 0, fontSize: '13px', color: '#374151', fontWeight: '600' }}>
                  {req.customerName}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>
                  {req.customerMobile}
                </p>
              </div>

              {/* Items */}
              <div style={{ marginBottom: '12px' }}>
                <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Order Items
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                  {req.items.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0, paddingRight: '12px' }}>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                          {item.itemName}
                        </p>
                        {item.specialNotes && (
                          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic' }}>
                            {item.specialNotes}
                          </p>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>× {item.quantity}</p>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                          ₹{(item.unitPrice * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  borderTop: '1px solid #E5E7EB',
                  paddingTop: '10px', marginBottom: '16px',
                }}
              >
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Total</span>
                <span style={{ fontSize: '16px', fontWeight: '800', color: '#1D4ED8' }}>
                  ₹{total.toFixed(2)}
                </span>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => onAccept(req.id)}
                  disabled={actioning}
                  style={{
                    flex: 1,
                    padding: '11px',
                    backgroundColor: actioning ? '#86EFAC' : '#22C55E',
                    color: '#fff',
                    border: 'none', borderRadius: '10px',
                    fontSize: '14px', fontWeight: '700',
                    cursor: actioning ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  }}
                >
                  {actioning ? '⏳' : '✓'} Accept
                </button>
                <button
                  onClick={() => onReject(req.id)}
                  disabled={actioning}
                  style={{
                    flex: 1,
                    padding: '11px',
                    backgroundColor: actioning ? '#FCA5A5' : '#EF4444',
                    color: '#fff',
                    border: 'none', borderRadius: '10px',
                    fontSize: '14px', fontWeight: '700',
                    cursor: actioning ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  }}
                >
                  ✕ Reject
                </button>
              </div>
            </>
          ) : call ? (
            <>
              {/* Waiter call body */}
              <div
                style={{
                  textAlign: 'center',
                  padding: '20px 0 16px',
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔔</div>
                <p style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                  {call.tableName}
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>
                  A customer is requesting waiter assistance at this table.
                </p>
              </div>

              <button
                onClick={() => onAcknowledge(call.id)}
                disabled={actioning}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: actioning ? '#FCD34D' : '#F59E0B',
                  color: '#fff',
                  border: 'none', borderRadius: '10px',
                  fontSize: '15px', fontWeight: '700',
                  cursor: actioning ? 'not-allowed' : 'pointer',
                  marginBottom: '8px',
                }}
              >
                {actioning ? 'Acknowledging…' : '✓ Acknowledge'}
              </button>

              <button
                onClick={onDismiss}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: 'transparent',
                  color: '#9CA3AF',
                  border: '1px solid #E5E7EB', borderRadius: '10px',
                  fontSize: '13px', fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Dismiss (handle later)
              </button>
            </>
          ) : null}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
    </div>
  );
};
